import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SupplierCategory } from '@bhops/shared';
import { suppliersApi } from '@/api/suppliers';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Input, Select } from '@/components/Form';
import { useToast } from '@/components/Toast';

// docs/plan/v0.5.0-telemetry-supplier-inventory-predictive/plan.md §4.5.

const CATEGORY_OPTIONS: Array<{ value: SupplierCategory; label: string }> = [
  { value: SupplierCategory.GRILLER_SPECIALIST, label: '그릴러 전문' },
  { value: SupplierCategory.LOADER_SPECIALIST, label: '로더 전문' },
  { value: SupplierCategory.SENSOR_SPECIALIST, label: '센서 전문' },
  { value: SupplierCategory.GENERAL, label: '일반' },
  { value: SupplierCategory.OVERSEAS, label: '해외' },
];

export interface SupplierCreateModalProps {
  open: boolean;
  onClose: () => void;
}

export function SupplierCreateModal({ open, onClose }: SupplierCreateModalProps) {
  const toast = useToast();
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [category, setCategory] = useState<SupplierCategory | ''>('');
  const [error, setError] = useState<string | null>(null);

  const createMx = useMutation({
    mutationFn: () =>
      suppliersApi.create({
        code: code.trim(),
        displayName: displayName.trim(),
        contactEmail: contactEmail.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        category: category || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success('공급사를 등록했습니다.');
      handleClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function handleClose(): void {
    if (createMx.isPending) return;
    setCode('');
    setDisplayName('');
    setContactEmail('');
    setContactPhone('');
    setCategory('');
    setError(null);
    onClose();
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setError(null);
    if (!code.trim()) return setError('공급사 코드를 입력해 주세요.');
    if (code.trim().length < 2) return setError('코드는 2자 이상 입력해 주세요.');
    if (!displayName.trim()) return setError('이름을 입력해 주세요.');
    createMx.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="공급사 등록"
      width={500}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={createMx.isPending}
          >
            취소
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={createMx.isPending}
            data-testid="supplier-create-submit"
          >
            {createMx.isPending ? '저장 중…' : '등록'}
          </Button>
        </>
      }
    >
      <form style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {error && <FormError>{error}</FormError>}

        <Field
          label="코드"
          htmlFor="code"
          required
          hint="사내에서 정한 코드입니다. 예: ABC-MFG-001"
        >
          <Input
            id="code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ABC-MFG-001"
            data-testid="supplier-code-input"
          />
        </Field>

        <Field label="이름" htmlFor="display-name" required>
          <Input
            id="display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="ABC Manufacturing Co."
            data-testid="supplier-name-input"
          />
        </Field>

        <Field label="분류 (선택)" htmlFor="category">
          <Select
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value as SupplierCategory | '')}
          >
            <option value="">— 선택 안 함 —</option>
            {CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="연락 이메일 (선택)" htmlFor="contact-email">
          <Input
            id="contact-email"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="orders@abc-mfg.com"
          />
        </Field>

        <Field label="연락 전화 (선택)" htmlFor="contact-phone">
          <Input
            id="contact-phone"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            placeholder="+82-2-1234-5678"
          />
        </Field>
      </form>
    </Modal>
  );
}
